<script setup lang="ts">
import ColorPicker from "./ColorPicker.vue";
import MergeTagTextarea from "./MergeTagTextarea.vue";
import SpacingControl from "./SpacingControl.vue";
import { useI18n } from "../composables/useI18n";
import type {
  PopupDesignSettings,
  SpacingValue,
  TemplateSettings,
} from "@aswin.dev/types";
import { resolvePopupEmbed } from "../utils/resolvePopupEmbed";
import {
  cardClass,
  inputClass,
  inputGroupInputClass,
  inputSuffixClass,
  labelClass,
  DEFAULT_BG_COLOR,
} from "../constants/styleConstants";
import { Circle, Eye, Globe, Info, Square } from "@lucide/vue";
import { computed } from "vue";
import { FONTS_MANAGER_KEY, requireInject } from "../keys";

const props = defineProps<{
  settings: TemplateSettings;
}>();

const emit = defineEmits<{
  (e: "update", settings: Partial<TemplateSettings>): void;
}>();

const PREHEADER_MAX_LENGTH = 150;

const { t } = useI18n();

const fontsManager = requireInject(FONTS_MANAGER_KEY, "TemplateSettings");
const fontFamilies = computed(() => fontsManager.fonts.value);

// If current font is not in available list (e.g., custom font when disabled), use default
const displayedFontFamily = computed(() => {
  const isAvailable = fontFamilies.value.some(
    (font) => font.value === props.settings.fontFamily,
  );
  return isAvailable
    ? props.settings.fontFamily
    : fontsManager.defaultFont.value;
});

const widthPresets = [
  { value: 480, label: "480px" },
  { value: 600, label: "600px" },
  { value: 700, label: "700px" },
  { value: 800, label: "800px" },
];

const hasPopupSettings = computed(() => Boolean(props.settings.popup));

const popupContentPadding = computed(() =>
  hasPopupSettings.value
    ? resolvePopupEmbed(props.settings).design.contentPadding
    : null,
);

function patchPopupDesign(patch: Partial<PopupDesignSettings>): void {
  const resolved = resolvePopupEmbed(props.settings);
  emit("update", {
    popup: {
      ...resolved,
      design: {
        ...resolved.design,
        ...patch,
      },
    },
  });
}

function onPopupContentPaddingUpdate(value: SpacingValue): void {
  patchPopupDesign({ contentPadding: value });
}

const backgroundOpacityPct = computed(() => {
  const raw = props.settings.backgroundOpacity;
  const n = typeof raw === "number" && Number.isFinite(raw) ? raw : 1;
  return Math.round(Math.min(1, Math.max(0, n)) * 100);
});

function onBackgroundOpacityInput(event: Event): void {
  const raw = Number((event.target as HTMLInputElement).value);
  const pct = Math.min(100, Math.max(0, Number.isFinite(raw) ? raw : 100));
  emit("update", { backgroundOpacity: pct / 100 });
}

/**
 * When `backgroundShadow` is unset we surface the legacy `--tpl-shadow-xl`
 * default as ~100% in the slider so users see a sensible starting point that
 * matches what's currently on screen, instead of "0%" with no visible shadow.
 */
const backgroundShadowPct = computed(() => {
  const raw = props.settings.backgroundShadow;
  const n = typeof raw === "number" && Number.isFinite(raw) ? raw : 1;
  return Math.round(Math.min(1, Math.max(0, n)) * 100);
});

function onBackgroundShadowInput(event: Event): void {
  const raw = Number((event.target as HTMLInputElement).value);
  const pct = Math.min(100, Math.max(0, Number.isFinite(raw) ? raw : 100));
  emit("update", { backgroundShadow: pct / 100 });
}
</script>

<template>
  <aside
    class="tpl:flex tpl:w-full tpl:flex-1 tpl:flex-col tpl:bg-[var(--tpl-bg-elevated)]"
  >
    <div
      class="tpl:flex tpl:flex-1 tpl:flex-col tpl:gap-3 tpl:overflow-y-auto tpl:p-4"
    >
      <!-- Layout card -->
      <div :class="cardClass">
        <div
          class="tpl:mb-3.5 tpl:flex tpl:items-center tpl:gap-2 tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          <Square
            class="tpl:text-[var(--tpl-text-muted)]"
            :size="14"
            :stroke-width="2"
          />
          <span>{{ t.templateSettings.layout }}</span>
        </div>

        <div class="tpl:mb-3.5">
          <label :class="labelClass">{{
            t.templateSettings.widthPreset
          }}</label>
          <div
            class="tpl:grid tpl:grid-cols-4 tpl:gap-1 tpl:rounded-[var(--tpl-radius-sm)] tpl:p-1 tpl:bg-[var(--tpl-bg-hover)]"
          >
            <button
              v-for="preset in widthPresets"
              :key="preset.value"
              class="tpl:cursor-pointer tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:px-2 tpl:py-1.5 tpl:text-sm tpl:font-medium tpl:transition-all tpl:duration-[120ms] tpl:ease-[cubic-bezier(0.16,1,0.3,1)]"
              :style="{
                backgroundColor:
                  settings.width === preset.value
                    ? 'var(--tpl-bg)'
                    : 'transparent',
                color:
                  settings.width === preset.value
                    ? 'var(--tpl-primary)'
                    : 'var(--tpl-text-muted)',
                boxShadow:
                  settings.width === preset.value
                    ? 'var(--tpl-shadow)'
                    : 'none',
              }"
              @click="emit('update', { width: preset.value })"
            >
              {{ preset.label }}
            </button>
          </div>
        </div>

        <div>
          <label :class="labelClass">{{
            t.templateSettings.customWidth
          }}</label>
          <div class="tpl:flex tpl:items-stretch">
            <input
              type="number"
              :class="inputGroupInputClass"
              :value="settings.width"
              min="300"
              max="900"
              @input="
                emit('update', {
                  width: Number(($event.target as HTMLInputElement).value),
                })
              "
            />
            <span :class="inputSuffixClass">px</span>
          </div>
        </div>

        <div v-if="hasPopupSettings && popupContentPadding" class="tpl:mt-4">
          <SpacingControl
            :model-value="popupContentPadding"
            :label="t.templateSettings.contentPadding"
            @update:model-value="onPopupContentPaddingUpdate"
          />
        </div>
      </div>

      <!-- Appearance card -->
      <div :class="cardClass">
        <div
          class="tpl:mb-3.5 tpl:flex tpl:items-center tpl:gap-2 tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          <Circle
            class="tpl:text-[var(--tpl-text-muted)]"
            :size="14"
            :stroke-width="2"
          />
          <span>{{ t.templateSettings.appearance }}</span>
        </div>

        <div class="tpl:mb-3.5">
          <label :class="labelClass">{{
            t.templateSettings.backgroundColor
          }}</label>
          <ColorPicker
            :model-value="settings.backgroundColor"
            :placeholder="DEFAULT_BG_COLOR"
            @update:model-value="emit('update', { backgroundColor: $event })"
          />
        </div>

        <div class="tpl:mb-3.5 tpl:space-y-2">
          <div class="tpl:flex tpl:items-center tpl:justify-between">
            <label :class="labelClass" for="template-bg-opacity">
              {{ t.templateSettings.backgroundOpacity }}
            </label>
            <span
              class="tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:tabular-nums"
            >
              {{ backgroundOpacityPct }}%
            </span>
          </div>
          <input
            id="template-bg-opacity"
            type="range"
            class="tpl:w-full tpl:accent-[var(--tpl-primary)]"
            min="0"
            max="100"
            step="5"
            :value="backgroundOpacityPct"
            @input="onBackgroundOpacityInput"
          />
        </div>

        <div class="tpl:mb-3.5 tpl:space-y-2">
          <div class="tpl:flex tpl:items-center tpl:justify-between">
            <label :class="labelClass" for="template-bg-shadow">
              {{ t.templateSettings.backgroundShadow }}
            </label>
            <span
              class="tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:tabular-nums"
            >
              {{ backgroundShadowPct }}%
            </span>
          </div>
          <input
            id="template-bg-shadow"
            type="range"
            class="tpl:w-full tpl:accent-[var(--tpl-primary)]"
            min="0"
            max="100"
            step="5"
            :value="backgroundShadowPct"
            @input="onBackgroundShadowInput"
          />
        </div>

        <div>
          <label :class="labelClass">{{ t.templateSettings.fontFamily }}</label>
          <select
            :class="inputClass"
            :value="displayedFontFamily"
            @change="
              emit('update', {
                fontFamily: ($event.target as HTMLSelectElement).value,
              })
            "
          >
            <option
              v-for="font in fontFamilies"
              :key="font.value"
              :value="font.value"
            >
              {{ font.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Language card -->
      <div :class="cardClass">
        <div
          class="tpl:mb-3.5 tpl:flex tpl:items-center tpl:gap-2 tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          <Globe
            class="tpl:text-[var(--tpl-text-muted)]"
            :size="14"
            :stroke-width="2"
          />
          <span>{{ t.templateSettings.language }}</span>
        </div>

        <div>
          <label :class="labelClass">{{
            t.templateSettings.contentLocale
          }}</label>
          <input
            type="text"
            :class="inputClass"
            :value="settings.locale ?? ''"
            placeholder="en"
            spellcheck="false"
            autocapitalize="off"
            autocomplete="off"
            @input="
              emit('update', {
                locale:
                  ($event.target as HTMLInputElement).value.trim() || undefined,
              })
            "
          />
          <p
            class="tpl:mt-1 tpl:text-xs tpl:leading-relaxed tpl:text-[var(--tpl-text-dim)]"
          >
            {{ t.templateSettings.contentLocaleHint }}
          </p>
        </div>
      </div>

      <!-- Preheader card -->
      <div :class="cardClass">
        <div
          class="tpl:mb-3.5 tpl:flex tpl:items-center tpl:gap-2 tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text)]"
        >
          <Eye
            class="tpl:text-[var(--tpl-text-muted)]"
            :size="14"
            :stroke-width="2"
          />
          <span>{{ t.templateSettings.preheaderText }}</span>
        </div>

        <div>
          <MergeTagTextarea
            :model-value="settings.preheaderText ?? ''"
            :placeholder="t.templateSettings.preheaderTextPlaceholder"
            :rows="2"
            @update:model-value="
              emit('update', {
                preheaderText: $event.replace(/[\r\n]/g, ' ') || undefined,
              })
            "
          />
          <div
            class="tpl:mt-1 tpl:flex tpl:items-start tpl:justify-between tpl:gap-2"
          >
            <span
              class="tpl:text-xs tpl:leading-relaxed tpl:text-[var(--tpl-text-dim)]"
            >
              {{ t.templateSettings.preheaderTextHint }}
            </span>
            <span
              class="tpl:shrink-0 tpl:text-xs tpl:tabular-nums tpl:text-[var(--tpl-text-dim)]"
            >
              {{ (settings.preheaderText ?? "").length }}/{{
                PREHEADER_MAX_LENGTH
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Tips card -->
      <div
        class="tpl:rounded-[var(--tpl-radius)] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-3"
      >
        <div
          class="tpl:mb-2.5 tpl:flex tpl:items-center tpl:gap-1.5 tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text-muted)]"
        >
          <Info :size="14" :stroke-width="2" />
          <span>{{ t.templateSettings.tips }}</span>
        </div>
        <ul
          class="tpl:m-0 tpl:pl-[18px] tpl:text-xs tpl:leading-relaxed tpl:text-[var(--tpl-text-dim)]"
        >
          <li class="tpl:mb-1 tpl:last:mb-0">
            {{ t.templateSettings.tip1 }}
          </li>
          <li class="tpl:mb-1 tpl:last:mb-0">
            {{ t.templateSettings.tip2 }}
          </li>
          <li class="tpl:mb-1 tpl:last:mb-0">
            {{ t.templateSettings.tip3 }}
          </li>
        </ul>
      </div>
    </div>
  </aside>
</template>
